/**
 * Where everything goes on a chart: title, legend, axis titles, tick labels, plot area.
 *
 * Pure geometry in viewBox units. A chart is drawn into a fixed viewBox (640 wide by
 * default) and scaled by the browser, so these numbers are the ones that end up in the
 * exported SVG and PNG — the preview and the file are the same picture.
 *
 * The sizes follow APA 7's advice for figure text (sans-serif, 8–14 pt once placed in a
 * paper). Placed at the full 6.5-inch text width of a paper, a 640-unit chart prints at
 * about 0.73 pt per unit: 11-unit value labels at 8 pt, 12-unit tick labels near 9 pt,
 * the 18-unit title near 13 pt.
 */

import {
  estimateTextWidth,
  maxTextWidth,
  type TextStyle,
  truncateText,
  wrapText,
} from "@/lib/tool-kit/charts/text";

/** Font sizes in viewBox units. */
export const CHART_FONT_SIZE = {
  title: 18,
  axisTitle: 13,
  tick: 12,
  legend: 12,
  value: 11,
} as const;

/** The default drawing size. Charts that grow with their data change the height. */
export const CHART_WIDTH = 640;
export const CHART_HEIGHT = 400;

/** Space between the edge of the image and anything drawn. */
export const CHART_PADDING = 16;
/** Length of a tick mark. */
export const TICK_SIZE = 5;
/** Gap between a tick mark and its label. */
export const TICK_GAP = 4;
export const LINE_HEIGHT = 1.25;

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TitleBlock {
  lines: string[];
  /** Centre of the lines. */
  x: number;
  /** Baseline of the first line. */
  y: number;
  lineHeight: number;
  fontSize: number;
  /** Vertical space the block takes, including the gap under it. */
  height: number;
}

/** A centred bold title of at most two lines, or null for no title. */
export function layoutTitle(
  title: string,
  width: number,
  top = CHART_PADDING
): TitleBlock | null {
  const text = title.trim();
  if (text === "") {
    return null;
  }
  const fontSize = CHART_FONT_SIZE.title;
  const lines = wrapText(
    text,
    width - 2 * CHART_PADDING,
    { fontSize, bold: true },
    2
  );
  const lineHeight = fontSize * LINE_HEIGHT;
  return {
    lines,
    x: width / 2,
    y: top + fontSize,
    lineHeight,
    fontSize,
    height: lines.length * lineHeight + 10,
  };
}

/* ------------------------------------------------------------------- legend */

export const LEGEND_SWATCH = 12;
const LEGEND_TEXT_GAP = 6;
const LEGEND_ITEM_GAP = 18;

export interface LegendItemLayout {
  label: string;
  /** Index into the items passed in — the series index. */
  index: number;
  /** Left edge of the swatch, relative to the legend's left edge. */
  x: number;
  /** Top of the row, relative to the legend's top. */
  y: number;
}

export interface LegendLayout {
  items: LegendItemLayout[];
  width: number;
  height: number;
  rowHeight: number;
}

/**
 * Lay out legend entries in centred rows that wrap within `maxWidth` — or, with
 * `oneColumn`, one left-aligned entry per row (a legend beside a pie). Long labels are
 * shortened with an ellipsis rather than allowed to run off the image.
 */
export function layoutLegend(
  labels: readonly string[],
  maxWidth: number,
  {
    fontSize = CHART_FONT_SIZE.legend,
    oneColumn = false,
  }: { fontSize?: number; oneColumn?: boolean } = {}
): LegendLayout {
  const style: TextStyle = { fontSize };
  const rowHeight = Math.max(LEGEND_SWATCH, fontSize) + 8;
  const maxLabel = maxWidth - LEGEND_SWATCH - LEGEND_TEXT_GAP;
  const rows: { label: string; index: number; width: number }[][] = [[]];
  const rowWidths = [0];
  for (const [index, raw] of labels.entries()) {
    const label = truncateText(raw, maxLabel, style);
    const width =
      LEGEND_SWATCH + LEGEND_TEXT_GAP + estimateTextWidth(label, style);
    const row = rows.length - 1;
    const needed = rowWidths[row] === 0 ? width : width + LEGEND_ITEM_GAP;
    const full = oneColumn || rowWidths[row] + needed > maxWidth;
    if (rowWidths[row] > 0 && full) {
      rows.push([{ label, index, width }]);
      rowWidths.push(width);
    } else {
      rows[row].push({ label, index, width });
      rowWidths[row] += needed;
    }
  }
  const items: LegendItemLayout[] = [];
  for (const [rowIndex, row] of rows.entries()) {
    let x = oneColumn ? 0 : (maxWidth - rowWidths[rowIndex]) / 2;
    for (const entry of row) {
      items.push({
        label: entry.label,
        index: entry.index,
        x,
        y: rowIndex * rowHeight,
      });
      x += entry.width + LEGEND_ITEM_GAP;
    }
  }
  const used = labels.length === 0 ? 0 : rows.length;
  return {
    items,
    width: Math.max(0, ...rowWidths),
    height: used * rowHeight,
    rowHeight,
  };
}

/* -------------------------------------------------------- category labels */

export interface CategoryLabelLayout {
  /** True when the labels are drawn at −45° because they do not fit level. */
  rotated: boolean;
  /** Each label's lines (one line when rotated). */
  lines: string[][];
  /** Height of the label band under the axis. */
  height: number;
}

const ROTATE_45 = Math.SQRT1_2;

/**
 * Labels under a row of bars: level and wrapped onto up to two lines when every word
 * fits its slot, otherwise rotated −45° and shortened to `maxRotatedWidth`.
 */
export function layoutCategoryLabels(
  labels: readonly string[],
  slotWidth: number,
  {
    fontSize = CHART_FONT_SIZE.tick,
    maxRotatedWidth = 180,
  }: { fontSize?: number; maxRotatedWidth?: number } = {}
): CategoryLabelLayout {
  const style: TextStyle = { fontSize };
  const available = Math.max(8, slotWidth - 6);
  const lineHeight = fontSize * LINE_HEIGHT;
  const wrapped = labels.map((label) => wrapText(label, available, style));
  const fitsLevel = labels.every(
    (label, index) =>
      wrapped[index].length <= 2 &&
      label
        .split(" ")
        .every((word) => estimateTextWidth(word, style) <= available)
  );
  if (fitsLevel) {
    const most = Math.max(1, ...wrapped.map((lines) => lines.length));
    return { rotated: false, lines: wrapped, height: most * lineHeight };
  }
  const lines = labels.map((label) => [
    truncateText(label, maxRotatedWidth, style),
  ]);
  const widest = maxTextWidth(
    lines.map((entry) => entry[0]),
    style
  );
  return {
    rotated: true,
    lines,
    height: (widest + fontSize) * ROTATE_45 + 2,
  };
}

/**
 * The image height for a chart whose category labels take `labelHeight` under the plot:
 * the standard height, grown by whatever rotated labels need beyond a normal band, so
 * long labels take room from the page rather than from the plot.
 */
export function heightForLabels(labelHeight: number): number {
  return CHART_HEIGHT + Math.max(0, Math.round(labelHeight - 48));
}

/* -------------------------------------------------------------- the frame */

export interface FrameInput {
  width: number;
  height: number;
  /** The title drawn inside the image; leave empty for APA figures. */
  title?: string;
  xTitle?: string;
  yTitle?: string;
  /** Height of a legend drawn between the title and the plot. */
  legendHeight?: number;
  /** Width of the widest label left of the plot (value ticks or category names). */
  leftLabelWidth: number;
  /** Height of the label band under the plot. */
  bottomLabelHeight: number;
  /** Extra room right of the plot — for a value label past the longest bar. */
  rightPadding?: number;
  /** Extra room above the plot — for a value label above the tallest bar. */
  topPadding?: number;
}

export interface AxisTitle {
  text: string;
  x: number;
  y: number;
}

export interface Frame {
  width: number;
  height: number;
  title: TitleBlock | null;
  /** Top of the legend, if there is one. */
  legendTop: number;
  plot: Rect;
  /** Centred under the plot. */
  xTitle: AxisTitle | null;
  /** Rotated −90° about (x, y), centred beside the plot. */
  yTitle: AxisTitle | null;
}

const AXIS_TITLE_BAND = CHART_FONT_SIZE.axisTitle * LINE_HEIGHT + 8;

/**
 * Carve the image into title, legend, axis-title bands and the plot rectangle.
 *
 * Every chart with axes is laid out by this one function, so their margins agree and a
 * long tick label pushes the plot over instead of being clipped.
 */
export function cartesianFrame(input: FrameInput): Frame {
  const { width, height } = input;
  const title = layoutTitle(input.title ?? "", width);
  let top = CHART_PADDING + (title?.height ?? 0);
  const legendTop = top;
  if (input.legendHeight) {
    top += input.legendHeight + 6;
  }
  top += input.topPadding ?? 8;
  const xText = input.xTitle?.trim() ?? "";
  const yText = input.yTitle?.trim() ?? "";
  const left =
    CHART_PADDING +
    (yText ? AXIS_TITLE_BAND : 0) +
    input.leftLabelWidth +
    TICK_SIZE +
    TICK_GAP;
  const right = width - CHART_PADDING - (input.rightPadding ?? 8);
  const bottom =
    height -
    CHART_PADDING -
    (xText ? AXIS_TITLE_BAND : 0) -
    input.bottomLabelHeight -
    TICK_SIZE -
    TICK_GAP;
  const plot: Rect = {
    x: left,
    y: top,
    width: Math.max(40, right - left),
    height: Math.max(40, bottom - top),
  };
  const axisStyle: TextStyle = { fontSize: CHART_FONT_SIZE.axisTitle };
  return {
    width,
    height,
    title,
    legendTop,
    plot,
    xTitle: xText
      ? {
          text: truncateText(xText, plot.width, axisStyle),
          x: plot.x + plot.width / 2,
          y: height - CHART_PADDING - 2,
        }
      : null,
    yTitle: yText
      ? {
          text: truncateText(yText, plot.height, axisStyle),
          x: CHART_PADDING + CHART_FONT_SIZE.axisTitle,
          y: plot.y + plot.height / 2,
        }
      : null,
  };
}

/**
 * The image height that gives a plot of `plotHeight`, for charts whose height follows
 * their data (one row per category). Inverts `cartesianFrame`'s vertical arithmetic.
 */
export function heightForPlot(
  plotHeight: number,
  input: Omit<FrameInput, "height">
): number {
  const probe = cartesianFrame({ ...input, height: 10_000 });
  return Math.ceil(10_000 - probe.plot.height + plotHeight);
}

/* --------------------------------------------------------------- filenames */

const COMBINING_MARKS = /\p{M}/gu;
const NOT_FILENAME_SAFE = /[^a-z0-9]+/g;
const EDGE_DASHES = /^-+|-+$/g;

/** `Mean Score by Group` → `mean-score-by-group`, or `fallback` for a title with no ASCII. */
export function chartFilename(title: string, fallback: string): string {
  const slug = title
    .normalize("NFKD")
    .replace(COMBINING_MARKS, "")
    .toLowerCase()
    .replace(NOT_FILENAME_SAFE, "-")
    .replace(EDGE_DASHES, "")
    .slice(0, 60)
    .replace(EDGE_DASHES, "");
  return slug || fallback;
}

/**
 * The corner of `plot` where a `width` × `height` box covers the fewest points — where
 * an annotation such as a regression equation hides the least data. Ties go to the
 * first corner in reading order: top left, top right, bottom left, bottom right.
 */
export function emptiestCorner(
  plot: Rect,
  width: number,
  height: number,
  points: readonly (readonly [number, number])[],
  { inset = 4, margin = 6 }: { inset?: number; margin?: number } = {}
): { x: number; y: number } {
  const left = plot.x + inset;
  const right = plot.x + plot.width - inset - width;
  const top = plot.y + inset;
  const bottom = plot.y + plot.height - inset - height;
  const corners = [
    { x: left, y: top },
    { x: right, y: top },
    { x: left, y: bottom },
    { x: right, y: bottom },
  ];
  let best = corners[0];
  let fewest = Number.POSITIVE_INFINITY;
  for (const corner of corners) {
    const covered = points.filter(
      ([px, py]) =>
        px >= corner.x - margin &&
        px <= corner.x + width + margin &&
        py >= corner.y - margin &&
        py <= corner.y + height + margin
    ).length;
    if (covered < fewest) {
      best = corner;
      fewest = covered;
    }
  }
  return best;
}

/**
 * How much further right the plot must start so the first category's rotated label
 * stays inside the image. Rotated −45° labels run down and to the left of their slot,
 * so a long first label can reach past the left edge; `firstCenter` is the distance
 * from the plot's left edge to that slot's centre.
 */
export function rotatedOverflow(
  frame: Frame,
  labels: CategoryLabelLayout,
  firstCenter: number
): number {
  if (!labels.rotated) {
    return 0;
  }
  const widest = maxTextWidth(
    labels.lines.map((lines) => lines[0] ?? ""),
    { fontSize: CHART_FONT_SIZE.tick }
  );
  const reach = widest * ROTATE_45 + 4;
  return Math.max(0, Math.ceil(reach - (frame.plot.x + firstCenter - 4)));
}
